import { Component, OnInit, signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import { Currency, ExchangeRate, ExchangeRateHistoryCommand, SourceExchangeRate } from '../../models/exchange-rate/exchange-rate';
import { ExchangeRateProxyService } from '../../proxies/exchange-rate.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { UserAuthenticationService } from '../../services/user-authentication.service';
import { ExchangeRateRoles } from '../../models/roles/roles-group';
import { RATE_DECIMALS } from '../../utilities/settlement-rate.util';
@Component({
  selector: 'app-exchange-rate',
  templateUrl: './exchange-rate.component.html',
})
export class ExchangeRateComponent implements OnInit {
  /** Rate precision for the rate inputs — the API's (CurrencyConverter.RateDecimals, #5359). */
  protected readonly rateDecimals = RATE_DECIMALS;

  editing = signal(false);
  originalExchangeRate = signal<ExchangeRate | null>(null);
  exchangeRates = signal<ExchangeRate[]>([]);
  showCreateForm = signal(false);

  // Form Controls
  rateToUSD = new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]);
  buyRateToUSD = new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]);
  sellRateToUSD = new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]);
  financialInstitution = new FormControl<string | null>(null);
  effectiveDate = new FormControl<Date | null>(null, [Validators.required]);
  currency = new FormControl<number>({ value: Currency.HNL, disabled: true }, [Validators.required]); // Default HNL, disabled
  source = new FormControl<number>({ value: SourceExchangeRate.MANUAL, disabled: true }, [Validators.required]); // Default MANUAL, disabled

  // Search Controls — a start/end range over the whole history (#5225). Both default to null, so the
  // page opens on every rate on record rather than on today's, which is what a history view is for.
  searchFromDate = new FormControl<Date | null>(null);
  searchToDate = new FormControl<Date | null>(null);
  searchCurrency = new FormControl<number>({
    value: Currency.HNL,
    disabled: true,
  }); // Default HNL, disabled

  // ---- paging (server-side; rates accrue a row per currency per day forever) ----
  readonly totalRecords = signal(0);
  readonly pageSize = signal(25);
  readonly currentPage = signal(1);
  readonly loading = signal(false);

  // Options for dropdowns
  currencyOptions = signal<{ label: string; value: number }[]>([
    { label: 'USD', value: Currency.USD },
    { label: 'HNL', value: Currency.HNL },
  ]);

  sourceOptions = signal<{ label: string; value: number }[]>([
    { label: 'Manual', value: SourceExchangeRate.MANUAL },
    { label: 'API', value: SourceExchangeRate.API },
  ]);

  isReadOnly = false;

  constructor(
    private exchangeRateProxyService: ExchangeRateProxyService,
    private translateService: TranslateService,
    private userAuthenticationService: UserAuthenticationService,
  ) {
    this.isReadOnly = !this.userAuthenticationService.roles.includes(ExchangeRateRoles.ExchangeRateAdmin);
  }

  ngOnInit(): void {
    // Opens on the whole history (first page), not on today — see searchFromDate/searchToDate.
    this.searchExchangeRates();
    // Deprecated rateToUSD mirrors the buy (compra) rate that existing consumers expect.
    this.buyRateToUSD.valueChanges.subscribe(() => this.syncRateToUsd());
  }

  /**
   * The average of buy + sell (Promedio de Compra y Venta); null until both are set.
   *
   * The plain mean of the two numbers on screen, in the one orientation this screen uses throughout
   * (HNL per 1 USD).
   */
  averageRate(): number | null {
    const buy = this.buyRateToUSD.value;
    const sell = this.sellRateToUSD.value;
    return buy && sell && buy > 0 && sell > 0 ? (buy + sell) / 2 : null;
  }

  private syncRateToUsd(): void {
    const buy = this.buyRateToUSD.value;
    if (buy && buy > 0) this.rateToUSD.setValue(buy, { emitEvent: false });
  }

  /** Reloads from page 1. Use on an explicit Search, so a new filter cannot land the user on page 7 of 3. */
  searchExchangeRates(): void {
    this.currentPage.set(1);
    this.loadHistory();
  }

  /**
   * Fetches ONE page from the server (#5225). Paged server-side rather than pulling the lot and
   * letting the table slice it: the history grows by a row per currency per day and never shrinks.
   */
  private loadHistory(): void {
    const command: ExchangeRateHistoryCommand = {
      fromDate: this.searchFromDate.value ? this.formatDateToYYYYMMDD(this.searchFromDate.value) : null,
      toDate: this.searchToDate.value ? this.formatDateToYYYYMMDD(this.searchToDate.value) : null,
      currency: this.searchCurrency.value ?? null,
    };

    this.loading.set(true);
    this.exchangeRateProxyService.getHistory(command, this.currentPage(), this.pageSize()).subscribe({
      next: (result) => {
        this.loading.set(false);
        if (result.success && result.data) {
          this.exchangeRates.set(result.data.items ?? []);
          this.totalRecords.set(result.data.totalCount ?? 0);
        }
      },
      error: () => {
        this.loading.set(false);
        this.exchangeRates.set([]);
        this.totalRecords.set(0);
      },
    });
  }

  /** p-table lazy load: first is a row offset, so it converts to a 1-based page number. */
  onLazyLoad(event: { first?: number; rows?: number }): void {
    const rows = event.rows ?? this.pageSize();
    this.pageSize.set(rows);
    this.currentPage.set(Math.floor((event.first ?? 0) / rows) + 1);
    this.loadHistory();
  }

  clearSearch(): void {
    this.searchFromDate.reset();
    this.searchToDate.reset();
    this.searchExchangeRates();
  }

  getCurrentRate(): void {
    if (this.searchCurrency.value !== null) {
      this.exchangeRateProxyService.getByCurrency(this.searchCurrency.value).subscribe({
        next: (result) => {
          if (result.success && result.data) {
            this.exchangeRates.set([result.data]);
          }
        },
      });
    }
  }

  showCreateFormToggle(): void {
    this.showCreateForm.set(true);
    this.resetForm();
    this.editing.set(false);
    this.originalExchangeRate.set(null);
  }

  editExchangeRate(exchangeRate: ExchangeRate): void {
    this.editing.set(true);
    this.originalExchangeRate.set(exchangeRate);
    this.showCreateForm.set(true);
    this.populateControls(exchangeRate);
  }

  populateControls(exchangeRate: ExchangeRate): void {
    // No conversion: rates are STORED as HNL per 1 USD (~24.70), which is already the orientation the
    // form asks for and the one CurrencyConverter reads. The reciprocal (~0.04) is the GL's posting
    // multiplier and belongs nowhere near this screen.
    this.buyRateToUSD.setValue(exchangeRate.buyRateToUSD ?? null);
    this.sellRateToUSD.setValue(exchangeRate.sellRateToUSD ?? null);
    this.rateToUSD.setValue(exchangeRate.rateToUSD ?? null);
    this.financialInstitution.setValue(exchangeRate.financialInstitution ?? null);
    this.effectiveDate.setValue(new Date(exchangeRate.effectiveDate));
    this.currency.setValue(exchangeRate.currency);
    this.source.setValue(exchangeRate.source);
  }

  resetForm(): void {
    this.rateToUSD.reset();
    this.buyRateToUSD.reset();
    this.sellRateToUSD.reset();
    this.financialInstitution.reset();
    this.effectiveDate.reset();
    this.currency.setValue(Currency.HNL); // Reset to default
    this.source.setValue(SourceExchangeRate.MANUAL); // Reset to default
  }

  cancel(): void {
    this.showCreateForm.set(false);
    this.editing.set(false);
    this.originalExchangeRate.set(null);
    this.resetForm();
  }

  save(): void {
    if (this.isFormInvalid()) {
      GlobalMessageUtil.warn('Form Invalid', 'Please fill all required fields with valid values.');
      return;
    }

    // Stored exactly as typed — HNL per 1 USD. See populateControls: there is no orientation change
    // anywhere on this screen, because the database already holds the quoted form.
    const buy = this.buyRateToUSD.value ?? 0;
    const sell = this.sellRateToUSD.value ?? 0;
    const legacyRate = this.rateToUSD.value ?? 0;
    const hasBuySell = buy > 0 && sell > 0;

    const exchangeRate: ExchangeRate = {
      id: this.originalExchangeRate()?.id ?? '',
      currency: this.currency.value!,
      rateToUSD: hasBuySell ? buy : legacyRate, // legacy = buy (compra)
      buyRateToUSD: hasBuySell ? buy : 0,
      sellRateToUSD: hasBuySell ? sell : 0,
      financialInstitution: this.financialInstitution.value,
      effectiveDate: this.formatDateToYYYYMMDD(this.effectiveDate.value!),
      source: this.source.value!,
    };

    if (!this.editing()) {
      this.createExchangeRate(exchangeRate);
    } else {
      this.updateExchangeRate(exchangeRate);
    }
  }

  private createExchangeRate(exchangeRate: ExchangeRate): void {
    this.exchangeRateProxyService.createExchangeRate(exchangeRate).subscribe({
      next: (result) => {
        if (result.success) {
          const currencyText = this.getCurrencyText(this.searchCurrency.value ?? Currency.HNL);
          GlobalMessageUtil.success(
            this.translateService.instant('Exchange_Rate_Created_Title'),
            this.translateService.instant('Exchange_Rate_Created', {
              currency: currencyText,
            }),
          );
          this.cancel();
          this.searchExchangeRates(); // Refresh the list
        }
      },
    });
  }

  private updateExchangeRate(exchangeRate: ExchangeRate): void {
    this.exchangeRateProxyService.updateExchangeRate(this.originalExchangeRate()?.id ?? '', exchangeRate).subscribe({
      next: (result) => {
        if (result.success) {
          const currencyText = this.getCurrencyText(this.searchCurrency.value ?? Currency.HNL);
          GlobalMessageUtil.success(
            this.translateService.instant('Exchange_Rate_Updated_Title').replace('{{currency}}', currencyText),
            this.translateService.instant('Exchange_Rate_Updated').replace('{{currency}}', currencyText),
          );
          this.cancel();
          this.searchExchangeRates(); // Refresh the list
        }
      },
    });
  }

  private isFormInvalid(): boolean {
    return (
      this.buyRateToUSD.invalid || this.sellRateToUSD.invalid || this.effectiveDate.invalid || this.currency.invalid || this.source.invalid
    );
  }

  private formatDateToYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getCurrencyText(currencyValue: number): string {
    const currency = this.currencyOptions().find((option) => option.value === currencyValue);
    return currency ? currency.label : 'Unknown';
  }
}
